import { AbstractWarehouseDataSyncStepWorker } from './abstract-warehouse-worker.utils';
import { expose } from 'comlink';
import { collection, deleteDoc, doc, DocumentSnapshot, getDocs, getFirestore, Query, query, setDoc, where } from 'firebase/firestore';
import { WarehouseSyncStepTaskContext } from '../executors';
import type { ProgressCallback } from '../executors';
import { SavedSearch } from '../../saved-search/saved-search.dto';
import { QueryDocumentSnapshot } from '@firebase/firestore';
import { dtoConverter, SAVED_SEARCH_STORE, updateBatch, WW_SAVED_SEARCH_COLLECTION, logger } from '@damofire/data-commons';

export class WarehouseSavedSearchesStepsWorker extends AbstractWarehouseDataSyncStepWorker<SavedSearch> {
  constructor() {
    super(SAVED_SEARCH_STORE, 'SAVED SEARCHES');
  }

  protected async clean(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseSavedSearchesStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<SavedSearch>(this.store, ctx, (dto) => dto.division._id, undefined, onProgress);
  }

  protected async push(ctx: WarehouseSyncStepTaskContext, onProgress?: (progress: number) => void): Promise<void> {
    logger.debug('WarehouseSavedSearchesStepsWorker::push', { division: ctx.division, category: 'sync' });
    return super.push(ctx, onProgress);
  }

  protected async resolveRemoteData(ctx: WarehouseSyncStepTaskContext, lastSnapshot: DocumentSnapshot, allFromIndex: SavedSearch[]): Promise<[DocumentSnapshot, number]> {
    const systemSearches = await this.getSystemSearches(ctx);
    const userSearches = await this.getUserSearches(ctx);

    const docs = [...systemSearches, ...userSearches];
    if (docs.length && !this.shouldStopTask) {
      const savedSearches = docs.map((d) => d.data());
      logger.info('Downloaded saved searches from Firestore', { count: savedSearches.length, category: 'sync' });
      await this.idbService.updateAll(SAVED_SEARCH_STORE, savedSearches).catch(console.error);
      return [null, docs.length];
    }

    return [null, 0];
  }

  private async getSystemSearches(ctx: WarehouseSyncStepTaskContext): Promise<QueryDocumentSnapshot<SavedSearch>[]> {
    logger.debug('Fetching system searches for division', { division: ctx.division, category: 'sync' });
    const q: Query = query(collection(getFirestore(), WW_SAVED_SEARCH_COLLECTION), where('division._id', '==', ctx.division), where('system', '==', true)).withConverter(
      dtoConverter({ syncId: ctx.syncId }),
    );
    const queryResult = await getDocs(q);
    return queryResult.docs;
  }

  private async getUserSearches(ctx: WarehouseSyncStepTaskContext): Promise<QueryDocumentSnapshot<SavedSearch>[]> {
    logger.debug('Fetching user searches for division', { division: ctx.division, userId: this.userId, category: 'sync' });
    const q: Query = query(
      collection(getFirestore(), WW_SAVED_SEARCH_COLLECTION),
      where('division._id', '==', ctx.division),
      where('audit.createdBy', '==', this.userId),
    ).withConverter(dtoConverter({ syncId: ctx.syncId }));
    const queryResult = await getDocs(q);
    return queryResult.docs;
  }

  protected async addRemoteData(data: SavedSearch[]): Promise<void> {
    for (const record of data) {
      await setDoc(doc(getFirestore(), `${WW_SAVED_SEARCH_COLLECTION}/${record._id}`), record);
    }
  }

  protected async deleteRemoteData(data: SavedSearch[]): Promise<void> {
    for (const record of data) {
      await deleteDoc(doc(getFirestore(), `${WW_SAVED_SEARCH_COLLECTION}/${record._id}`));
    }
  }

  protected async updateRemoteData(data: SavedSearch[]): Promise<void> {
    await updateBatch(WW_SAVED_SEARCH_COLLECTION, data);
  }

  protected updateTaskProgress(warehouse: string, downloaded: number, onProgress: ProgressCallback): Promise<void> {
    onProgress(0, { processed: downloaded });
    return Promise.resolve(undefined);
  }
}

expose(WarehouseSavedSearchesStepsWorker);
